import { useSignal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import { IcoCalendar } from "../icons";
import { RadioCard, RadioCardContent } from "./radio-card";

export type ScheduleFrequency = "one-time" | "weekly" | "biweekly" | "monthly";

export const DAYS = [
  { key: "SU", label: "S" },
  { key: "MO", label: "M" },
  { key: "TU", label: "T" },
  { key: "WE", label: "W" },
  { key: "TH", label: "T" },
  { key: "FR", label: "F" },
  { key: "SA", label: "S" },
] as const;

export const MONTHLY_OPTIONS = [
  { value: "FREQ=MONTHLY;BYMONTHDAY=1", label: "1st of the month" },
  { value: "FREQ=MONTHLY;BYMONTHDAY=15", label: "15th of the month" },
  { value: "FREQ=MONTHLY;BYDAY=1MO", label: "First Monday" },
  { value: "FREQ=MONTHLY;BYDAY=1FR", label: "First Friday" },
  { value: "FREQ=MONTHLY;BYDAY=-1MO", label: "Last Monday" },
  { value: "FREQ=MONTHLY;BYDAY=-1FR", label: "Last Friday" },
] as const;

/** Parse an RRULE string into frequency and selected days */
export function parseRrule(rrule: string | null): {
  frequency: ScheduleFrequency;
  days: Set<string>;
  monthlyValue: string | null;
} {
  if (!rrule) {
    return { frequency: "one-time", days: new Set(), monthlyValue: null };
  }

  if (rrule.includes("FREQ=MONTHLY")) {
    return { frequency: "monthly", days: new Set(), monthlyValue: rrule };
  }

  const isBiweekly = rrule.includes("INTERVAL=2");
  const frequency: ScheduleFrequency = isBiweekly ? "biweekly" : "weekly";
  const byDayMatch = rrule.match(/BYDAY=([A-Z,]+)/);
  const days = new Set(byDayMatch?.[1]?.split(",") ?? []);

  return { frequency, days, monthlyValue: null };
}

/** Build an RRULE string from frequency and selected days */
export function buildRrule(
  frequency: ScheduleFrequency,
  days: Set<string>,
  monthlyValue: string | null,
): string | null {
  if (frequency === "one-time") return null;
  if (frequency === "monthly") return monthlyValue;
  if (days.size === 0) return null;

  const orderedDays = DAYS.map((d) => d.key).filter((d) => days.has(d));
  const byDay = orderedDays.join(",");
  const interval = frequency === "biweekly" ? ";INTERVAL=2" : "";

  return `FREQ=WEEKLY${interval};BYDAY=${byDay}`;
}

type ScheduleSelectorProps = {
  rrule: string | null;
  onChange: (rrule: string | null) => void;
  /** Legend text for the fieldset. Defaults to "Frequency" */
  legend?: string;
};

export function ScheduleSelector({
  rrule,
  onChange,
  legend = "Frequency",
}: ScheduleSelectorProps) {
  const parsed = parseRrule(rrule);
  const frequency = useSignal<ScheduleFrequency>(parsed.frequency);
  const selectedDays = useSignal<Set<string>>(parsed.days);
  const monthlyValue = useSignal<string | null>(parsed.monthlyValue);

  // Sync local state when rrule prop changes
  useEffect(() => {
    const newParsed = parseRrule(rrule);
    frequency.value = newParsed.frequency;
    selectedDays.value = newParsed.days;
    monthlyValue.value = newParsed.monthlyValue;
  }, [rrule]);

  const updateRrule = (
    freq: ScheduleFrequency,
    days: Set<string>,
    monthly: string | null,
  ) => {
    onChange(buildRrule(freq, days, monthly));
  };

  const setFrequency = (freq: ScheduleFrequency) => {
    frequency.value = freq;
    if (freq === "monthly") {
      const monthly = monthlyValue.value || MONTHLY_OPTIONS[0].value;
      monthlyValue.value = monthly;
      updateRrule(freq, selectedDays.value, monthly);
    } else {
      updateRrule(freq, selectedDays.value, null);
    }
  };

  const toggleDay = (day: string) => {
    const newDays = new Set(selectedDays.value);
    if (newDays.has(day)) {
      newDays.delete(day);
    } else {
      newDays.add(day);
    }
    selectedDays.value = newDays;
    updateRrule(frequency.value, newDays, null);
  };

  const setMonthlyOption = (val: string) => {
    monthlyValue.value = val;
    updateRrule("monthly", selectedDays.value, val);
  };

  const showDaySelector =
    frequency.value === "weekly" || frequency.value === "biweekly";
  const showMonthlyOptions = frequency.value === "monthly";

  return (
    <fieldset class="flex flex-col gap-4">
      <legend class="text-sm font-medium text-text-secondary mb-1">
        {legend}
      </legend>

      <div class="flex gap-2">
        <RadioCard
          name="frequency"
          value="one-time"
          checked={frequency.value === "one-time"}
          onChange={() => setFrequency("one-time")}
          compact
        >
          <RadioCardContent label="Once" />
        </RadioCard>
        <RadioCard
          name="frequency"
          value="weekly"
          checked={frequency.value === "weekly"}
          onChange={() => setFrequency("weekly")}
          compact
        >
          <RadioCardContent label="Weekly" />
        </RadioCard>
        <RadioCard
          name="frequency"
          value="biweekly"
          checked={frequency.value === "biweekly"}
          onChange={() => setFrequency("biweekly")}
          compact
        >
          <RadioCardContent label="Biweekly" />
        </RadioCard>
        <RadioCard
          name="frequency"
          value="monthly"
          checked={frequency.value === "monthly"}
          onChange={() => setFrequency("monthly")}
          compact
        >
          <RadioCardContent label="Monthly" />
        </RadioCard>
      </div>

      {showDaySelector && (
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-text-secondary">On</span>
          <div class="flex gap-1">
            {DAYS.map((day) => (
              <DayChip
                key={day.key}
                label={day.label}
                selected={selectedDays.value.has(day.key)}
                onClick={() => toggleDay(day.key)}
              />
            ))}
          </div>
        </div>
      )}

      {showMonthlyOptions && (
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-text-secondary">On</span>
          <div class="grid grid-cols-2 gap-2">
            {MONTHLY_OPTIONS.map((opt) => (
              <RadioCard
                key={opt.value}
                name="monthlyOn"
                value={opt.value}
                checked={monthlyValue.value === opt.value}
                onChange={() => setMonthlyOption(opt.value)}
                compact
              >
                <RadioCardContent label={opt.label} />
              </RadioCard>
            ))}
          </div>
        </div>
      )}
    </fieldset>
  );
}

function DayChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`size-9 rounded-full text-sm font-medium transition-colors ${
        selected
          ? "bg-text text-surface"
          : "bg-surface-muted text-text-muted hover:bg-surface-hover hover:text-text"
      }`}
    >
      {label}
    </button>
  );
}

type DatePickerProps = {
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder: string;
};

export function DatePicker({
  label,
  value,
  onChange,
  placeholder,
}: DatePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const formatDateForInput = (date: Date | null): string => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().slice(0, 10);
  };

  const formatDateForDisplay = (date: Date | null): string => {
    if (!date) return placeholder;
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleChange = (e: Event) => {
    const val = (e.target as HTMLInputElement).value;
    if (!val) {
      onChange(null);
    } else {
      const parts = val.split("-").map(Number);
      const year = parts[0] ?? 0;
      const month = parts[1] ?? 1;
      const day = parts[2] ?? 1;
      const date = new Date(year, month - 1, day);
      // Validate the date is valid and reasonable (year 1900-2200)
      if (Number.isNaN(date.getTime()) || year < 1900 || year > 2200) {
        onChange(null);
      } else {
        onChange(date);
      }
    }
  };

  const handleClick = () => {
    inputRef.current?.showPicker();
  };

  const displayValue = formatDateForDisplay(value);
  const isPlaceholder = !value;

  return (
    <div class="flex flex-col gap-1">
      <span class="text-sm font-medium text-text-secondary">{label}</span>
      <button
        type="button"
        onClick={handleClick}
        class="relative flex items-center gap-3 px-3 py-2 rounded-lg border border-border bg-surface cursor-pointer hover:border-text-subtle focus:ring-2 focus:ring-ring transition-colors text-left"
      >
        <IcoCalendar class="size-4 text-text-subtle flex-shrink-0" />
        <span class={isPlaceholder ? "text-text-muted" : "text-text"}>
          {displayValue}
        </span>
      </button>
      <input
        ref={inputRef}
        type="date"
        value={formatDateForInput(value)}
        onChange={handleChange}
        class="sr-only"
      />
    </div>
  );
}
